import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldAlert, Loader2, RefreshCw, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin-only page for rotating the Apollo.io API key without a re-publish.
 * DB-first: `apollo_search.py` reads `app_settings.apollo_config` before
 * falling back to the APOLLO_API_KEY env var.
 */
export default function ApolloConfigAdmin() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/apollo-config");
      setInfo(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!apiKey.trim() || apiKey.trim().length < 8) {
      return toast.error("API key looks too short.");
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/apollo-config", { api_key: apiKey.trim() });
      toast.success(`Saved — active source is now "${data.active.source}"`);
      setApiKey("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/apollo-config/test", {});
      setTestResult(data);
      toast.success("Apollo key is working ✓");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="apollo-config-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-neutral-900 flex items-center gap-3">
          <Zap className="w-7 h-7 text-orange-500" /> Apollo.io credentials
        </h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-xl leading-relaxed">
          Rotate the Apollo API key used for B2B lead search. Changes are saved to the database
          and take effect immediately — no re-publish needed. Get a key at{" "}
          <a href="https://app.apollo.io/#/settings/integrations/api" target="_blank" rel="noreferrer"
             className="underline hover:text-black">
            Apollo → Settings → Integrations → API
          </a>.
        </p>
      </div>

      {/* Active status */}
      <div className="bg-white border border-neutral-200 p-6 mb-6" data-testid="apollo-active-card">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Currently active</div>
          <button onClick={load} disabled={loading} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1" data-testid="apollo-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : info ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <StatLine label="Source"     value={info.source === "db" ? "🟢 DB override" : info.source === "env" ? "🟡 Env fallback" : "🔴 Not configured"} />
            <StatLine label="Configured" value={info.configured ? "Yes" : "No"} />
            <StatLine label="Key suffix" value={info.key_last4 ? `…${info.key_last4}` : "—"} mono />
            <StatLine label="Env key present" value={info.env_key_present ? "yes" : "no"} />
          </div>
        ) : null}
      </div>

      {/* Update form */}
      <form onSubmit={save} className="bg-white border border-neutral-200 p-6 mb-6" data-testid="apollo-form">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Update credentials</div>
        <label className="block mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">New API key</div>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="paste your Apollo key…"
            className="lf-input font-mono w-full"
            data-testid="apollo-input-key"
            autoComplete="off"
          />
        </label>
        <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 p-3 mb-4">
          <ShieldAlert className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div>The key must have the <span className="font-mono">mixed_people_search</span> scope enabled. Free-tier keys get 60 req/min; paid plans unlock enrichment & bulk endpoints.</div>
        </div>
        <button type="submit" disabled={saving} className="lf-btn-primary inline-flex items-center gap-2" data-testid="apollo-save-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save credentials"}
        </button>
      </form>

      {/* Smoke test */}
      <div className="bg-white border border-neutral-200 p-6" data-testid="apollo-test-card">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Verify key</div>
        <button onClick={test} disabled={testing || !info?.configured} className="lf-btn-outline inline-flex items-center gap-2" data-testid="apollo-test-btn">
          <Zap className="w-4 h-4" /> {testing ? "Checking…" : "Ping Apollo profile"}
        </button>
        {testResult && (
          <pre className="mt-4 text-xs bg-neutral-50 border border-neutral-200 p-3 overflow-x-auto font-mono" data-testid="apollo-test-output">
            {JSON.stringify(testResult, null, 2)}
          </pre>
        )}
        <p className="text-xs text-neutral-500 mt-3">Calls Apollo's zero-credit profile endpoint. A successful response confirms the key is live and authorised.</p>
      </div>
    </div>
  );
}

function StatLine({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm text-neutral-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
